import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    checkUsernameAvailability, 
    validateUsername,
} from '../services/authService';
import { GoogleLogin } from '@react-oauth/google';
import '../styles/AuthPage.css';
import { FcGoogle } from 'react-icons/fc';
import { IoArrowBack } from 'react-icons/io5';
import { useAuth } from '../context/AuthContext';

const AuthPage = () => {
    const { 
        registerAndLogin, 
        login, 
        googleLogin: contextGoogleLogin,
        isLoading: authIsLoading,
        authError,
        token: idToken
    } = useAuth();

    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [username, setUsername] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [acceptPrivacy, setAcceptPrivacy] = useState(false);
    const [usernameError, setUsernameError] = useState('');
    const [isCheckingUsername, setIsCheckingUsername] = useState(false);
    const navigate = useNavigate();

    const validateUsernameInput = async (value) => {
        setIsCheckingUsername(true);
        setUsernameError('');
        const validation = validateUsername(value);
        if (!validation.valid) {
            setUsernameError(validation.error);
            setIsCheckingUsername(false);
            return false;
        }
        const { available, error: availError } = await checkUsernameAvailability(value);
        if (availError) {
            setUsernameError(availError);
            setIsCheckingUsername(false);
            return false;
        }
        if (!available) {
            setUsernameError('This username is already taken');
            setIsCheckingUsername(false);
            return false;
        }
        setIsCheckingUsername(false);
        return true;
    };

    const isFormValid = () => {
        if (isLogin) return email && password;
        return email && password && username && !usernameError && acceptTerms && acceptPrivacy && displayName; 
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        let result;
        if (!isLogin) {
            if (!isFormValid()) {
                return;
            }
            const isUsernameValid = await validateUsernameInput(username);
            if (!isUsernameValid) {
                return;
            }
            result = await registerAndLogin(email, password, username, displayName || username);
        } else {
            if (!isFormValid()) {
                return;
            }
            result = await login(email, password);
        }

        if (result && result.success) {
            navigate('/');
        }
    };

    const handleGoogleLoginSuccess = async (tokenResponse) => {
        console.log('Google Token Response:', tokenResponse);
        if (!tokenResponse || !tokenResponse.credential) {
            console.error('Credential not found in Google token response', tokenResponse);
            return;
        }
        const result = await contextGoogleLogin(tokenResponse.credential);
        if (result && result.success) {
            navigate('/');
        }
    };

    const handleGoBack = () => {
        navigate('/');
    };

    return (
        <div className="auth-container">
            <div className="auth-box">
                <button 
                    className="back-button" 
                    onClick={handleGoBack}
                    type="button"
                >
                    <IoArrowBack /> Back to Home
                </button>
                
                <h2>{isLogin ? 'Welcome Back' : 'Create Account'}</h2>
                <p className="auth-subtitle">
                    {isLogin ? 'Sign in to continue' : 'Register to get started'}
                </p>

                <form onSubmit={handleSubmit}>
                    {!isLogin && (
                        <>
                            <div className="form-group">
                                <input
                                    type="text"
                                    placeholder="Username"
                                    value={username}
                                    onChange={(e) => {
                                        setUsername(e.target.value);
                                        if (e.target.value) validateUsernameInput(e.target.value);
                                    }}
                                    className={usernameError ? 'error' : ''}
                                    required={!isLogin}
                                />
                                {isCheckingUsername && <span className="checking-username">Checking username...</span>}
                                {usernameError && <span className="error-message">{usernameError}</span>}
                            </div>
                            <div className="form-group display-name-group"> 
                                <input
                                    type="text"
                                    placeholder="Nickname (Display Name)"
                                    value={displayName}
                                    onChange={(e) => setDisplayName(e.target.value)}
                                    required={!isLogin}
                                />
                                <p className="privacy-disclaimer">
                                    This name will be publicly visible next to your reviews. 
                                    For your privacy, we recommend not using your real name.
                                </p>
                            </div>
                        </>
                    )}
                    <div className="form-group">
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>
                    <div className="form-group">
                        <input
                            type="password"
                            placeholder="Password"
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            required
                        />
                    </div>

                    {!isLogin && (
                        <>
                            <div className="form-group terms-group">
                                <input 
                                    type="checkbox" 
                                    id="acceptTerms" 
                                    checked={acceptTerms} 
                                    onChange={(e) => setAcceptTerms(e.target.checked)} 
                                    required={!isLogin}
                                />
                                <label htmlFor="acceptTerms">I accept the <a href="/terms" target="_blank" rel="noopener noreferrer">Terms and Conditions</a></label>
                            </div>
                            <div className="form-group terms-group">
                                <input 
                                    type="checkbox" 
                                    id="acceptPrivacy" 
                                    checked={acceptPrivacy} 
                                    onChange={(e) => setAcceptPrivacy(e.target.checked)} 
                                    required={!isLogin}
                                />
                                <label htmlFor="acceptPrivacy">I accept the <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a></label>
                            </div>
                        </>
                    )}

                    {authError && <p className="error-message main-error">{typeof authError === 'object' ? JSON.stringify(authError) : authError}</p>}

                    <button 
                        type="submit" 
                        className={`auth-button ${!isFormValid() || authIsLoading ? 'disabled' : ''}`}
                        disabled={!isFormValid() || authIsLoading}
                    >
                        {authIsLoading ? 'Loading...' : isLogin ? 'Sign In' : 'Create Account'}
                    </button>

                    <div className="divider">or</div>

                    <div className="google-login-container">
                        <GoogleLogin
                            onSuccess={handleGoogleLoginSuccess}
                            onError={() => {
                                console.error('Google Login Failed');
                            }}
                            useOneTap
                        />
                    </div>
                </form>

                <p className="toggle-auth">
                    {isLogin ? "Don't have an account? " : "Already have an account? "}
                    <button 
                        type="button"
                        className="toggle-button"
                        onClick={() => {
                            setIsLogin(!isLogin);
                            setUsernameError('');
                        }}
                    >
                        {isLogin ? 'Sign Up' : 'Sign In'}
                    </button>
                </p>
            </div>
        </div>
    );
};

export default AuthPage; 